import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Search, Loader2, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import {
  retrouverParPdl, peutRetrouver, FORME_PDL_ENEDIS,
  MESSAGE_VERIFICATION_INJOIGNABLE, MESSAGE_TROP_DE_VERIFICATIONS,
} from '@/shared/api/pdl';

// Le sens RETOUR : le numero est deja saisi, on demande a Enedis ce qu'il en sait.
// UN seul exemplaire, pose dans les TROIS portes -- le CRM, le wizard installateur et
// le formulaire QR.
//
// POURQUOI UN COMPOSANT ET PAS TROIS BOUTONS
//
// Exactement la raison de `RecherchePdl` a cote : trois ecrans qui poseraient la meme
// question finiraient par y repondre avec trois phrases differentes, et le jour d'un
// correctif un seul des trois le recevrait. Ici l'enjeu est plus grand encore : le
// bouton declenche une DECLARATION DE CONSENTEMENT chez un tiers.
//
// CE QU'IL NE FAIT JAMAIS
//
// 1. Reclamer autre chose que le numero. ⚠️ IL N'EXIGEAIT CINQ CHAMPS QUE PARCE QU'IL
//    POSAIT UN CONSENTEMENT AVANT DE LIRE — mesure du 09/09 : `POST /recette/requete`
//    n'a besoin que du numero, et rend l'adresse complete sur un compteur vierge de tout
//    consentement. Le nom, quand il est la, CONFIRME que le compteur est bien celui du
//    client ; il n'autorise plus rien. Le consentement, lui, reste recueilli a sa place,
//    a la fin du parcours, avec son texte et son empreinte.
// 2. Remplir en silence. Chaque champ corrige est nomme, avec ce qu'il valait avant, et
//    « revenir a ma saisie » revient vraiment.
// 3. Ecrire un nom. Le nom du titulaire ne revient JAMAIS d'un numero de compteur --
//    mesure du 07/09 sur 14 combinaisons d'endpoints, il est `null` partout. Ce que le
//    serveur rend, c'est la CONFIRMATION que le nom saisi est bien celui du titulaire.
//    Il ne se decouvre pas, il se confirme. L'ADRESSE, elle, est ecrite meme sans cette
//    confirmation depuis l'arbitrage du 07/09 -- mais jamais celle d'un compteur dont on
//    sait qu'il appartient a quelqu'un d'autre.
// 4. Empecher de saisir. Le champ reste modifiable, toujours.

// Comment chaque champ corrige se nomme a l'ecran. Le nom technique ne dit rien a un
// poseur, et « complement_adresse : ESC A 3 ETG D » se lirait comme une erreur.
const LIBELLES = {
  adresse: 'Adresse',
  complement_adresse: "Complement d'adresse",
  code_postal: 'Code postal',
  ville: 'Ville',
};

export default function RetrouverParPdl({ client, onCorrigerChamps,
                                          authentifie = false,
                                          testid = 'pdl-retrouver' }) {
  const [enCours, setEnCours] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [echec, setEchec] = useState('');
  const [corrections, setCorrections] = useState(null);

  const champs = {
    pdl: client.pdl_prm,
    nom: client.nom,
    prenom: client.prenom,
    email: client.email,
    adresse: client.adresse,
    complementAdresse: client.complement_adresse,
    codePostal: client.code_postal,
    ville: client.ville,
    clientId: client.id,
  };
  const pret = peutRetrouver(champs);
  // Un numero saisi mais mal forme se dit AVANT de partir : les premiers chiffres 6, 7
  // et 8 sont refuses par construction chez Enedis, et l'appel serait un refus certain.
  const numeroSaisi = String(client.pdl_prm || '').trim();
  const numeroMalForme = numeroSaisi.length > 0 && !FORME_PDL_ENEDIS.test(numeroSaisi);

  const verifier = async () => {
    setEnCours(true);
    setEchec('');
    setVerdict(null);
    setCorrections(null);
    try {
      const resultat = await retrouverParPdl(champs, { authentifie });
      setVerdict(resultat);
      // ARBITRAGE DE BOUBACAR DU 07/09 : l'adresse remplit le formulaire meme quand le
      // nom n'a pas pu etre confirme. « Si le PDL saisi donne l'adresse c'est deja
      // suffisant, meme si on n'a pas le nom, au moins les adresses bien remplies. »
      //
      // Ce qui reste ferme, et c'est le serveur qui le tient : sur `autre_titulaire` il
      // ne rend AUCUN rapprochement, donc il n'y a rien a ecrire. Ce cas-la n'est pas
      // « on ne sait pas », c'est « on sait que ce compteur est celui d'un autre » --
      // et remplir l'adresse y ecrirait le logement du voisin.
      if (resultat.titulaire?.verdict !== 'autre_titulaire' && onCorrigerChamps) {
        const aEcrire = {};
        const avant = {};
        Object.entries(resultat.rapprochement?.champs || {}).forEach(([cle, etat]) => {
          if (!etat.proposition) return;
          avant[cle] = client[cle] || '';
          aEcrire[cle] = etat.proposition;
        });
        if (Object.keys(aEcrire).length) {
          onCorrigerChamps(aEcrire);
          setCorrections({ avant, apres: aEcrire });
        }
      }
    } catch (erreur) {
      setEchec(erreur?.response?.status === 429 ? MESSAGE_TROP_DE_VERIFICATIONS
        : MESSAGE_VERIFICATION_INJOIGNABLE);
    }
    setEnCours(false);
  };

  const titulaire = verdict?.titulaire?.verdict;
  const fiche = verdict?.fiche || {};

  return (
    <div className="space-y-1" data-testid={`${testid}-bloc`}>

      <Button
        type="button" variant="outline" size="sm" className="h-8 text-[12px]"
        onClick={verifier} disabled={enCours || !pret}
        data-testid={testid}
      >
        {enCours ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
          : <Search className="w-3.5 h-3.5 mr-1" />}
        Verifier ce compteur
      </Button>

      {numeroMalForme && (
        <p className="text-[10px] text-amber-700" data-testid={`${testid}-forme`}>
          Ce numero n&apos;a pas la forme d&apos;un point de livraison Enedis. Verifiez-le
          sur le compteur.
        </p>
      )}

      {!pret && !numeroMalForme && (
        <p className="text-[10px] text-neutral-400" data-testid={`${testid}-prerequis`}>
          Saisissez le numero du compteur, 14 chiffres releves sur le compteur.
        </p>
      )}

      {/* LE COMPTEUR EST CONFIRME AU NOM SAISI. C'est le seul cas ou l'adresse d'Enedis
          est rendue, et donc le seul ou quelque chose se remplit. */}
      {titulaire === 'confirme' && (
        <p className="text-[11px] text-emerald-700 flex items-start gap-1"
          data-testid={`${testid}-confirme`}>
          <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" />
          {verdict.message}
        </p>
      )}

      {/* ⚠️ LE CAS QUI JUSTIFIE TOUT CE COMPOSANT. Le nom saisi existe bien a cette
          adresse, mais sur un AUTRE compteur : le numero recopie est celui du voisin, et
          il servirait de cle de deduplication des clients. Rien n'est rempli. */}
      {titulaire === 'autre_titulaire' && (
        <p className="text-[11px] text-red-700 flex items-start gap-1 bg-red-50
                      border border-red-100 rounded px-2 py-1.5"
          data-testid={`${testid}-autre-titulaire`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
          {verdict.message}
        </p>
      )}

      {/* ⚠️ CE BANDEAU RESTE MEME QUAND LES CHAMPS ONT ETE REMPLIS, et c'est le point.
          Depuis l'arbitrage du 07/09 l'adresse est ecrite sans que le nom soit confirme.
          Sans cette ligne, l'ecran ferait passer une adresse NON VERIFIEE pour une
          adresse verifiee -- et le poseur ne reviendrait jamais la controler. */}
      {titulaire === 'non_confirme' && verdict?.trouve && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1 bg-amber-50
                      border border-amber-100 rounded px-2 py-1.5"
          data-testid={`${testid}-non-confirme`}>
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
          {verdict.message}
        </p>
      )}

      {/* CE QUE LE COMPTEUR EST, ET S'IL EST VIVANT. Rendu dans TOUS les cas ou Enedis a
          repondu, y compris sans titulaire confirme : un type et un etat de compteur ne
          designent personne, et un compteur resilie ou non communicant ne fera jamais
          remonter la moindre donnee. Mieux vaut l'apprendre sur le chantier que trois
          semaines plus tard sur un ecran vide. */}
      {verdict?.trouve && (fiche.compteur_type || fiche.compteur_etat) && (
        <p className="text-[11px] text-neutral-500" data-testid={`${testid}-compteur`}>
          Compteur
          {fiche.compteur_type ? ` ${fiche.compteur_type}` : ''}
          {fiche.compteur_etat ? ` · ${fiche.compteur_etat}` : ''}
        </p>
      )}

      {/* CHAQUE CHAMP CORRIGE EST NOMME, avec ce qu'il valait avant. Une correction
          silencieuse ferait signer un mandat sur une adresse que personne n'a relue. */}
      {corrections && (
        <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-100
                        rounded px-2 py-1.5 space-y-0.5"
          data-testid={`${testid}-corrections`}>
          <p className="flex items-start gap-1">
            <Info className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>Corrige d&apos;apres Enedis :</span>
          </p>
          {Object.entries(corrections.apres).map(([cle, valeur]) => (
            <p key={cle} className="pl-4" data-testid={`${testid}-correction-${cle}`}>
              <span className="text-neutral-500">{LIBELLES[cle] || cle}</span>
              {' : '}
              {corrections.avant[cle]
                ? <span className="line-through text-neutral-400">{corrections.avant[cle]}</span>
                : <span className="text-neutral-400">(vide)</span>}
              {' → '}
              <span className="font-medium">{valeur}</span>
            </p>
          ))}
          <button
            type="button"
            className="pl-4 underline text-neutral-500 hover:text-neutral-700"
            data-testid={`${testid}-annuler-corrections`}
            onClick={() => { onCorrigerChamps(corrections.avant); setCorrections(null); }}
          >
            Revenir a ma saisie
          </button>
        </div>
      )}

      {/* LA DECLARATION QUI N'A JAMAIS QUITTE LE SERVEUR. Le bouchon rend un identifiant
          qui a la forme d'un vrai et le meme etat. Trois consentements ont ete recueillis
          ainsi le 26/08, avec le nom et l'adresse electronique de titulaires reels, sans
          que rien a l'ecran ne le signale. */}
      {verdict?.consentement?.declaration_simulee && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1"
          data-testid={`${testid}-simule`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
          Verification SIMULEE : rien n&apos;a ete demande a Enedis.
        </p>
      )}

      {/* L'accord etait deja la. Le dire evite au poseur de croire qu'il vient de faire
          signer une seconde fois. */}
      {verdict?.consentement?.reutilise && (
        <p className="text-[10px] text-neutral-400" data-testid={`${testid}-reutilise`}>
          L&apos;accord de ce client pour ce compteur etait deja enregistre.
        </p>
      )}

      {verdict && !verdict.trouve && (
        <p className="text-[11px] text-amber-700 flex items-start gap-1"
          data-testid={`${testid}-message`}>
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
          {verdict.message}
        </p>
      )}

      {echec && (
        <p className="text-[11px] text-red-600 flex items-start gap-1"
          data-testid={`${testid}-message`}>
          <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
          {echec}
        </p>
      )}
    </div>
  );
}
